import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, FileSpreadsheet, Filter, Printer } from "lucide-react";
import api, { API } from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

// Phase G (Feb 2026): Attendance Report download (.xlsx) for Management + Super Admin.
export default function AttendanceReport() {
  const nav = useNavigate();
  const { t, lang } = useLang();

  const today = new Date().toISOString().slice(0, 10);
  const sevenAgo = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const [fromDate, setFromDate] = useState(sevenAgo);
  const [toDate, setToDate] = useState(today);
  const [role, setRole] = useState("all");
  const [managerId, setManagerId] = useState("");
  const [salesmanId, setSalesmanId] = useState("");
  const [districtId, setDistrictId] = useState("");

  const [managers, setManagers] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/users", { params: { role: "manager" } }).then(r => r.data || []).catch(() => []),
      api.get("/users", { params: { role: "salesman" } }).then(r => r.data || []).catch(() => []),
      api.get("/districts").then(r => r.data || []).catch(() => []),
    ]).then(([m, s, d]) => {
      setManagers(m); setSalesmen(s); setDistricts(d);
    });
  }, []);

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (role && role !== "all") params.role = role;
      if (managerId) params.manager_id = managerId;
      if (salesmanId) params.salesman_id = salesmanId;
      if (districtId) params.district_id = districtId;

      const res = await api.get("/reports/attendance.xlsx", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance_${fromDate}_to_${toDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(lang === "ta" ? "பதிவிறக்கப்பட்டது" : "Downloaded");
      setPreview({ at: new Date().toLocaleString(), size: Math.round(blob.size / 1024) });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "";
      toast.error(`${lang === "ta" ? "பதிவிறக்க முடியவில்ல" : "Download failed"}${detail ? `: ${String(detail).slice(0, 80)}` : ""}`);
    } finally {
      setDownloading(false);
    }
  };

  const openPrintView = async () => {
    // Tier 2 (Feb 2026): Fetch backend HTML with auth header, open as blob in new tab.
    // User then uses browser's native Print → Save as PDF.
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (role && role !== "all") params.role = role;
    if (managerId) params.manager_id = managerId;
    if (salesmanId) params.salesman_id = salesmanId;
    if (districtId) params.district_id = districtId;
    try {
      const res = await api.get("/reports/attendance.html", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      const w = window.open(url, "_blank");
      if (!w) toast.error(lang === "ta" ? "பாப்-அப் தடுக்கப்பட்டது" : "Pop-up blocked");
      // Don't revoke immediately — let the new tab finish loading
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(lang === "ta" ? "பதிவேற்ற முடியவில்ல" : "Could not open print view");
    }
  };

  const dispName = (item) => (lang === "ta" && item?.name_ta) ? item.name_ta : (item?.name || "");

  return (
    <div className="min-h-screen bg-[#F5F5F7] page-slide-in pb-10">
      <TopBar title={lang === "ta" ? "வருகை அறிக்கை" : "Attendance Report"} onBack={() => nav(-1)} />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5 fade-up">
        {/* Hero */}
        <div className="bg-gradient-to-r from-[#5856D6] to-[#0055FF] text-white rounded-3xl p-6 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/15 grid place-items-center shrink-0">
              <FileSpreadsheet className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-wider opacity-80 font-bold">
                {lang === "ta" ? "GPS + வருகை + விற்பனை" : "GPS + Attendance + Activity"}
              </div>
              <h1 className="heading-font text-2xl sm:text-3xl font-bold mt-1">
                {lang === "ta" ? "வருகை அறிக்கை பதிவிறக்கம்" : "Download Attendance Report"}
              </h1>
              <p className="text-sm opacity-90 mt-2 max-w-2xl">
                {lang === "ta"
                  ? "தேதி வரம்பு + பாத்திரம் + மேனேஜர் + சேல்ஸ்மேன் + மாவட்டம் வடிகட்டிகளுடன் Excel வடிவத்தில் பதிவிறக்கவும்."
                  : "Date range + role + manager + salesman + district filters. Excel (.xlsx) download with all GPS-derived metrics."}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl soft-shadow p-5 space-y-4" data-testid="ar-filters">
          <div className="flex items-center gap-2 text-[#1D1D1F] font-bold heading-font">
            <Filter className="w-4 h-4" /> {t("filters") || "Filters"}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={lang === "ta" ? "முதல் தேதி" : "From Date"}>
              <input
                type="date" value={fromDate} max={toDate}
                onChange={(e) => setFromDate(e.target.value)}
                data-testid="ar-from-date"
                className="w-full bg-[#F5F5F7] border border-gray-200 rounded-xl px-3 py-2.5 font-semibold text-[#1D1D1F]"
              />
            </Field>
            <Field label={lang === "ta" ? "கடைசி தேதி" : "To Date"}>
              <input
                type="date" value={toDate} min={fromDate}
                onChange={(e) => setToDate(e.target.value)}
                data-testid="ar-to-date"
                className="w-full bg-[#F5F5F7] border border-gray-200 rounded-xl px-3 py-2.5 font-semibold text-[#1D1D1F]"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={lang === "ta" ? "பாத்திரம்" : "Role"}>
              <select
                value={role} onChange={(e) => setRole(e.target.value)}
                data-testid="ar-role-select"
                className="w-full bg-[#F5F5F7] border border-gray-200 rounded-xl px-3 py-2.5 font-semibold text-[#1D1D1F]"
              >
                <option value="all">{t("all") || "All"}</option>
                <option value="salesman">{lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman"}</option>
                <option value="manager">{lang === "ta" ? "மேனேஜர்" : "Manager"}</option>
              </select>
            </Field>
            <Field label={lang === "ta" ? "மாவட்டம்" : "District"}>
              <select
                value={districtId} onChange={(e) => setDistrictId(e.target.value)}
                data-testid="ar-district-select"
                className="w-full bg-[#F5F5F7] border border-gray-200 rounded-xl px-3 py-2.5 font-semibold text-[#1D1D1F]"
              >
                <option value="">{t("all") || "All"}</option>
                {districts.map(d => <option key={d.id} value={d.id}>{dispName(d)}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={lang === "ta" ? "மேனேஜர்" : "Manager"}>
              <select
                value={managerId} onChange={(e) => { setManagerId(e.target.value); if (e.target.value) setSalesmanId(""); }}
                data-testid="ar-manager-select"
                className="w-full bg-[#F5F5F7] border border-gray-200 rounded-xl px-3 py-2.5 font-semibold text-[#1D1D1F]"
              >
                <option value="">{t("all") || "All"}</option>
                {managers.map(m => <option key={m.id} value={m.id}>{dispName(m)}</option>)}
              </select>
            </Field>
            <Field label={lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman"}>
              <select
                value={salesmanId} onChange={(e) => { setSalesmanId(e.target.value); if (e.target.value) setManagerId(""); }}
                data-testid="ar-salesman-select"
                className="w-full bg-[#F5F5F7] border border-gray-200 rounded-xl px-3 py-2.5 font-semibold text-[#1D1D1F]"
              >
                <option value="">{t("all") || "All"}</option>
                {salesmen.map(s => <option key={s.id} value={s.id}>{dispName(s)}</option>)}
              </select>
            </Field>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              onClick={downloadExcel}
              disabled={downloading || !fromDate || !toDate}
              data-testid="ar-download-btn"
              className="bg-[#34C759] hover:bg-[#28A745] text-white rounded-xl px-5 py-3 font-bold text-sm flex items-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              {downloading
                ? (lang === "ta" ? "பதிவிறக்குகிறது…" : "Downloading…")
                : (lang === "ta" ? "Excel பதிவிறக்கம்" : "Download Excel")}
            </button>
            <button
              onClick={openPrintView}
              data-testid="ar-print-btn"
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-xl px-5 py-3 font-bold text-sm flex items-center gap-2 transition"
            >
              <Printer className="w-4 h-4" />
              {lang === "ta" ? "அச்சிடு / PDF" : "Print / PDF"}
            </button>
            <button
              onClick={() => { setRole("all"); setManagerId(""); setSalesmanId(""); setDistrictId(""); setFromDate(sevenAgo); setToDate(today); }}
              data-testid="ar-reset-btn"
              className="bg-gray-100 hover:bg-gray-200 text-[#1D1D1F] rounded-xl px-5 py-3 font-bold text-sm transition"
            >
              {t("clear_filters") || "Reset"}
            </button>
          </div>
        </div>

        {/* Columns preview */}
        <div className="bg-white rounded-2xl soft-shadow p-5">
          <h3 className="heading-font font-bold mb-3">
            {lang === "ta" ? "Excel நெடுவரிசைகள்" : "Excel Columns"}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-[#86868B]">
            {[
              [lang === "ta" ? "தேதி" : "Date", "Calendar"],
              [lang === "ta" ? "ஊழியர் பெயர்" : "Employee Name", "User"],
              [lang === "ta" ? "பாத்திரம்" : "Role", "Briefcase"],
              [lang === "ta" ? "மேனேஜர்" : "Manager", "Crown"],
              [lang === "ta" ? "மாவட்டம்" : "District", "Map"],
              [lang === "ta" ? "முதல் GPS நேரம்" : "First GPS time", "Clock"],
              [lang === "ta" ? "கடைசி GPS நேரம்" : "Last GPS time", "Clock"],
              [lang === "ta" ? "செயல்படும் காலம்" : "Active Duration", "Hourglass"],
              [lang === "ta" ? "மொத்த தூரம் (km)" : "Total Distance (km)", "Route"],
              [lang === "ta" ? "முதல் கடை வருகை" : "First Shop Visit", "Store"],
              [lang === "ta" ? "கடைசி கடை வருகை" : "Last Shop Visit", "Store"],
              [lang === "ta" ? "மொத்த கடைகள்" : "Shops Visited", "Building"],
              [lang === "ta" ? "ஆர்டர்கள்" : "Orders", "Package"],
              [lang === "ta" ? "விற்பனை இல்ல" : "No-Sale", "X"],
              [lang === "ta" ? "விற்ற பெட்டிகள்" : "Boxes Sold", "Boxes"],
              [lang === "ta" ? "நிலை" : "Status", "Activity"],
              [lang === "ta" ? "செயலின்மை எச்சரிக்கைகள்" : "Inactivity Alerts", "Bell"],
              [lang === "ta" ? "இடம் பொருத்தமின்மை" : "Location Mismatch", "AlertTriangle"],
            ].map(([label]) => (
              <div key={label} className="px-2 py-1 bg-gray-50 rounded font-semibold">{label}</div>
            ))}
          </div>
          <p className="text-[11px] text-[#86868B] mt-3">
            {lang === "ta"
              ? "GPS + வருகை + ஆர்டர்/நோ-சேல் தரவுகளில் இருந்து கணக்கிடப்படுகிறது. நிலை: Present / Partial / Absent."
              : "Computed from GPS pings + attendance + orders/no-sales. Status: Present / Partial / Absent."}
          </p>
        </div>

        {preview && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-sm text-green-800" data-testid="ar-last-download">
            {lang === "ta" ? "கடைசி பதிவிறக்கம்" : "Last download"}: {preview.at} • {preview.size} KB
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-[#86868B] font-bold block mb-1">{label}</label>
    {children}
  </div>
);
